import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Trophy, Gamepad2, Calendar, Phone, AlertCircle, User, Shield, Monitor, Gamepad } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const PLAYING_POSITIONS = [
  { value: 'GK', label: 'GK - Portiere', labelEn: 'GK - Goalkeeper' },
  { value: 'DC', label: 'DC - Difensore Centrale', labelEn: 'DC - Center Back' },
  { value: 'CDC', label: 'CDC - Centrocampista Difensivo', labelEn: 'CDC - Defensive Midfielder' },
  { value: 'ES', label: 'ES - Esterno Sinistro', labelEn: 'ES - Left Winger' },
  { value: 'ED', label: 'ED - Esterno Destro', labelEn: 'ED - Right Winger' },
  { value: 'COC', label: 'COC - Centrocampista Offensivo', labelEn: 'COC - Attacking Midfielder' },
  { value: 'ATT', label: 'ATT - Attaccante', labelEn: 'ATT - Striker' },
];

const PLATFORMS = [
  { value: 'PS5', label: 'PS5', icon: Gamepad2, color: '#2979FF' },
  { value: 'Xbox', label: 'Xbox', icon: Gamepad, color: '#00E676' },
  { value: 'PC', label: 'PC', icon: Monitor, color: '#FF9100' },
];

const CompleteProfile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [gamertag, setGamertag] = useState('');
  const [playingPosition, setPlayingPosition] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [platform, setPlatform] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isItalian = i18n.language === 'it';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!playingPosition) {
      setError(isItalian ? 'Seleziona il tuo ruolo' : 'Please select your position');
      return;
    }

    if (!platform) {
      setError(isItalian ? 'Seleziona la tua piattaforma di gioco' : 'Please select your gaming platform');
      return;
    }

    setLoading(true);

    try {
      await authApi.completeProfile({
        gamertag,
        playing_position: playingPosition,
        birth_date: birthDate,
        phone,
        platform,
      });
      await refreshUser();
      toast.success(isItalian ? 'Profilo completato!' : 'Profile completed!');
      // Small delay to ensure state is updated before navigation
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
    } catch (err) {
      setError(err.response?.data?.detail || (isItalian ? 'Errore nel salvataggio' : 'Failed to save'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen ept-bg-ambient flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded bg-[#00F0FF] flex items-center justify-center">
            <Trophy className="w-7 h-7 text-white" />
          </div>
          <span className="font-heading font-bold text-2xl uppercase tracking-wider text-white">
            EPT ITALY
          </span>
        </div>

        <div className="bg-[#0a0d15] border border-white/10  p-8">
          {/* User Info */}
          {user && (
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10">
              <div className="w-14 h-14 rounded-full bg-[#050508] border border-white/10 flex items-center justify-center overflow-hidden">
                {user.picture ? (
                  <img src={user.picture} alt={user.gamertag || user.name} className="w-full h-full object-cover" />
                ) : (
                  <User size={24} className="text-slate-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-white">{user.gamertag ? `${user.gamertag}` : user.name}</p>
                <p className="text-sm text-slate-500">{user.email}</p>
              </div>
            </div>
          )}

          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-2 text-center">
            {isItalian ? 'Completa il Profilo' : 'Complete Your Profile'}
          </h1>
          <p className="text-slate-400 text-center mb-6">
            {isItalian 
              ? 'Inserisci i tuoi dati per iniziare a giocare' 
              : 'Enter your details to start playing'}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#FF1744]/10 border border-[#FF1744]/30  flex items-center gap-3">
              <AlertCircle className="text-[#FF1744]" size={20} />
              <p className="text-sm text-[#FF1744]">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Gamertag */}
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                Gamertag *
              </Label>
              <div className="relative">
                <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  type="text"
                  value={gamertag}
                  onChange={(e) => setGamertag(e.target.value)}
                  className="pl-10 h-12 bg-[#050508] border-white/10 text-white  focus:border-[#00F0FF] focus:ring-[#2979FF]"
                  placeholder={isItalian ? 'Il tuo gamertag unico' : 'Your unique gamertag'}
                  required
                  minLength={3}
                  maxLength={20}
                  pattern="^[a-zA-Z0-9_-]+$"
                  title={isItalian ? 'Solo lettere, numeri, _ e -' : 'Only letters, numbers, _ and -'}
                  data-testid="gamertag-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                {isItalian ? '3-20 caratteri, solo lettere, numeri, _ e -' : '3-20 characters, only letters, numbers, _ and -'}
              </p>
            </div>

            {/* Playing Position */}
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                {isItalian ? 'Ruolo Ricoperto' : 'Playing Position'} *
              </Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 z-10" size={18} />
                <Select value={playingPosition} onValueChange={setPlayingPosition}>
                  <SelectTrigger 
                    className="pl-10 h-12 bg-[#050508] border-white/10 text-white "
                    data-testid="position-select"
                  >
                    <SelectValue placeholder={isItalian ? 'Seleziona il tuo ruolo' : 'Select your position'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0d15] border-white/10 z-[100]">
                    {PLAYING_POSITIONS.map((pos) => (
                      <SelectItem key={pos.value} value={pos.value} className="text-white hover:bg-white/10 cursor-pointer">
                        {isItalian ? pos.label : pos.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Birth Date */}
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                {isItalian ? 'Data di Nascita' : 'Date of Birth'} *
              </Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="pl-10 h-12 bg-[#050508] border-white/10 text-white  focus:border-[#00F0FF] focus:ring-[#2979FF]"
                  required
                  max={new Date().toISOString().split('T')[0]}
                  data-testid="birthdate-input"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                {isItalian ? 'Numero di Telefono' : 'Phone Number'} *
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10 h-12 bg-[#050508] border-white/10 text-white  focus:border-[#00F0FF] focus:ring-[#2979FF]"
                  placeholder="+39 123 456 7890"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Platform */}
            <div className="space-y-2">
              <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                {isItalian ? 'Piattaforma di Gioco' : 'Gaming Platform'} *
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {PLATFORMS.map((p) => {
                  const Icon = p.icon;
                  const active = platform === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setPlatform(p.value)}
                      className={`h-12 flex items-center justify-center gap-2 border rounded transition-all font-bold text-sm uppercase tracking-wider ${
                        active
                          ? 'text-black border-transparent'
                          : 'bg-[#050508] border-white/10 text-slate-400 hover:text-white hover:border-white/30'
                      }`}
                      style={active ? { background: p.color } : {}}
                      data-testid={`platform-btn-${p.value.toLowerCase()}`}
                    >
                      <Icon size={16} />
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 btn-primary"
              disabled={loading}
              data-testid="complete-profile-submit"
            >
              {loading 
                ? (isItalian ? 'Salvataggio...' : 'Saving...') 
                : (isItalian ? 'Completa Profilo' : 'Complete Profile')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-slate-500 text-sm">
          {isItalian 
            ? 'I tuoi dati saranno salvati in modo sicuro' 
            : 'Your data will be saved securely'}
        </p>
      </motion.div>
    </div>
  );
};

export default CompleteProfile;
